<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import FormSection from "@/Components/FormSection.vue";
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import SearchSelect from "@/Components/SearchSelect.vue";
import SelectInput from "@/Components/SelectInput.vue";
import { computed, reactive, watch, ref } from "vue";

const props = defineProps({
    vendors: Array,
    clients: Array,
    styles: Array,
    colorOptions: Array,
    fabricOptions: Array,
    garmentOptions: Array,
    genderOptions: Array,
    typeOptions: Array,
});

const TYPE_STOCK = "";
const TYPE_CUSTOM = "CS";

const selectedVendor = ref(null)
const selectedClient = ref(null)

const vendorId = (vendor) => vendor?.id ?? vendor?.vendorID ?? vendor?.ID ?? null

const vendorOptions = computed(() =>
    (props.vendors || [])
        .filter((vendor) => vendorId(vendor) != null)
        .map((vendor) => {
            const code = vendor?.code ?? vendor?.vendor_code ?? ""
            const name = vendor?.name ?? vendor?.vendorName ?? ""
            const label = [code, name].filter(Boolean).join(" - ")

            return {
                label: label || String(name || code || vendorId(vendor)),
                value: Number(vendorId(vendor)),
            }
        })
        .filter((option) => option.label && !Number.isNaN(option.value))
)

const vendorModel = computed({
    get: () => (
        selectedVendor.value != null && selectedVendor.value !== ""
            ? Number(selectedVendor.value)
            : null
    ),
    set: (value) => {
        selectedVendor.value = value != null ? Number(value) : null
    },
})

const clientId = (client) => client?.id ?? client?.ID ?? null

const clientOptions = computed(() =>
    (props.clients || [])
        .filter((client) => clientId(client) != null)
        .map((client) => {
            const code = client?.code ?? client?.client_code ?? ""
            const name = client?.name ?? client?.client ?? ""
            const label = [code, name].filter(Boolean).join(" - ")

            return {
                label: label || String(name || code || clientId(client)),
                value: Number(clientId(client)),
            }
        })
        .filter((option) => option.label && !Number.isNaN(option.value))
)

const clientModel = computed({
    get: () => (
        selectedClient.value != null && selectedClient.value !== ""
            ? Number(selectedClient.value)
            : null
    ),
    set: (value) => {
        selectedClient.value = value != null ? Number(value) : null
    },
})

const state = reactive({
    selectedType: TYPE_STOCK,
    styleText: "",
    stockColor: null,
    customColor: "",
    fabricText: "",
    selectedGarment: null,
    selectedGender: null,
    code: "",
    copyFeedback: "",
});

const vendorCode = computed(() => {
    const id = selectedVendor.value;
    if (id == null || id === "") return "";
    const vendor = (props.vendors || []).find((item) => Number(vendorId(item)) === Number(id));
    return vendor?.code ?? vendor?.vendor_code ?? "";
});

const clientCode = computed(() => {
    const id = selectedClient.value;
    if (id == null || id === "") return "";
    const client = (props.clients || []).find((item) => Number(clientId(item)) === Number(id));
    return client?.code ?? client?.client_code ?? "";
});

const styleCode = computed(() => state.styleText || "");

const isStock = computed(() => state.selectedType === TYPE_STOCK);
const isCustom = computed(() => state.selectedType === TYPE_CUSTOM);

function first3(str) {
    if (!str) return "";
    return String(str).trim().substring(0, 3).toUpperCase();
}

const generatedCode = computed(() => {
    const typeVal = state.selectedType || "";
    const vendor = vendorCode.value || "";
    const client = clientCode.value || "";
    const style = styleCode.value || "";

    if (isStock.value) {
        const vendorPart = vendor || "";
        const stylePart = style || "";
        const colorPart = first3(state.stockColor) || "";
        const clientPart = client || "";

        let code = "";

        if (vendorPart) code += vendorPart;
        if (stylePart) code += stylePart;
        if (colorPart) code += "-" + colorPart;
        if (clientPart) code += clientPart;

        return code;
    }

    const gender = state.selectedGender || "";
    const garment = first3(state.selectedGarment);
    const fabric = state.fabricText || "";
    const color = state.customColor || "";

    const vendorPart = vendor || "";
    const stylePart = style || "";
    const colorPart = color || "";
    const clientPart = client || "";

    let base = "";

    if (typeVal) base += typeVal;
    if (vendorPart) base += vendorPart;
    if (stylePart) base += "-" + stylePart;
    if (gender) base += gender;
    if (clientPart) base += clientPart;

    const extras = `${garment}${colorPart}${fabric}`;

    return extras ? `${base}-${extras}` : base;
});

watch(
    () => [
        vendorCode.value,
        clientCode.value,
        state.selectedType,
        state.styleText,
        state.stockColor,
        state.customColor,
        state.fabricText,
        state.selectedGarment,
        state.selectedGender,
    ],
    () => {
        state.code = generatedCode.value;
    }
);

async function copyToClipboard() {
    if (!state.code) return;
    const text = state.code;

    if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
        try {
            await navigator.clipboard.writeText(text);
            state.copyFeedback = "Copied!";
        } catch (e) {
            console.error("Error with clipboard");
            fallbackCopy(text);
        }
    } else {
        fallbackCopy(text);
    }

    setTimeout(() => (state.copyFeedback = ""), 1500);
}

function fallbackCopy(text) {
    try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        ta.style.left = "-9999px";
        ta.style.top = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(ta);

        state.copyFeedback = ok ? "Copied!" : "Could not copy";
    } catch (err) {
        console.error("Fallback copy error:", err);
        state.copyFeedback = "Could not copy";
    }
}

function resetStyleCode() {
    selectedVendor.value = null;
    selectedClient.value = null;

    state.selectedType = TYPE_STOCK;
    state.styleText = "";
    state.stockColor = null;
    state.customColor = "";
    state.fabricText = "";
    state.selectedGarment = null;
    state.selectedGender = null;
    state.code = "";
    state.copyFeedback = "";
}
</script>

<template>
    <AppLayout title="Code Generator">
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                Style Code Generator
            </h2>
        </template>

        <div class="max-w-5xl mx-auto py-10 sm:px-6 lg:px-8 space-y-6">
            <FormSection>
                <template #title>
                    Type
                </template>

                <template #form>
                    <div class="col-span-6 sm:col-span-3">
                        <InputLabel for="type" value="Type" />
                        <SelectInput id="type" v-model="state.selectedType" class="mt-1">
                            <option :value="TYPE_STOCK">Stock</option>
                            <option :value="TYPE_CUSTOM">Custom</option>
                        </SelectInput>
                    </div>
                </template>
            </FormSection>

            <FormSection>
                <template #title>
                    Base data
                </template>
                <template #description>
                    Select attributes to generate the style code.
                </template>

                <template #form>
                    <div class="col-span-6 sm:col-span-3">
                        <InputLabel for="vendor-search" value="Vendor" />
                        <SearchSelect id="vendor-search" v-model="vendorModel" :options="vendorOptions"
                            placeholder="-- Select a Vendor --" filter-mode="includes" />
                    </div>

                    <div class="col-span-6 sm:col-span-3">
                        <InputLabel for="style-text" value="Style" />
                        <TextInput id="style-text" v-model="state.styleText" type="text" class="mt-1 block w-full"
                            autocomplete="off" />
                    </div>

                    <div class="col-span-6 sm:col-span-3" v-if="isCustom">
                        <InputLabel for="gender" value="Gender" />
                        <SelectInput id="gender" v-model="state.selectedGender" class="mt-1">
                            <option :value="null">Select gender</option>
                            <option v-for="opt in genderOptions" :key="opt.value" :value="opt.value">
                                {{ opt.label }}
                            </option>
                        </SelectInput>
                    </div>

                    <div class="col-span-6 sm:col-span-3" v-if="isStock">
                        <InputLabel for="color" value="Color" />
                        <template v-if="isStock">
                            <SelectInput id="color" v-model="state.stockColor" class="mt-1">
                                <option :value="null">Select color</option>
                                <option v-for="opt in colorOptions" :key="opt.value" :value="opt.value">
                                    {{ opt.label ?? opt.value }}
                                </option>
                            </SelectInput>
                        </template>
                    </div>

                    <div class="col-span-6 sm:col-span-3">
                        <InputLabel for="client-search" value="Client" />
                        <SearchSelect id="client-search" v-model="clientModel" :options="clientOptions"
                            placeholder="-- Select a Client --" filter-mode="includes" />
                    </div>

                    <div class="col-span-6 sm:col-span-3" v-if="isCustom">
                        <InputLabel for="garment" value="Garment" />
                        <SelectInput id="garment" v-model="state.selectedGarment" class="mt-1">
                            <option :value="null">Select garment</option>
                            <option v-for="opt in garmentOptions" :key="opt.value" :value="opt.value">
                                {{ opt.label ?? opt.value }}
                            </option>
                        </SelectInput>
                    </div>

                    <div class="col-span-6 sm:col-span-3" v-if="!isStock">
                        <InputLabel for="color" value="Color" />
                        <template v-if="!isStock">
                            <TextInput id="color" v-model="state.customColor" type="text" class="mt-1 block w-full"
                                autocomplete="off" />
                        </template>
                    </div>

                    <div class="col-span-6 sm:col-span-3" v-if="isCustom">
                        <InputLabel for="fabric" value="Fabric Code" />
                        <TextInput id="fabric" v-model="state.fabricText" type="text" class="mt-1 block w-full"
                            autocomplete="off" />
                    </div>

                </template>
            </FormSection>

            <FormSection>
                <template #title>
                    Generated code
                </template>
                <template #description>
                    The field is editable. Click copy to send it to the clipboard.
                </template>
                <template #form>
                    <div class="col-span-6 sm:col-span-6">
                        <InputLabel for="generated_code" value="Code" />
                        <div class="flex items-center space-x-2 mt-1">
                            <TextInput id="generated_code" v-model="state.code" type="text"
                                class="block w-full font-mono" autocomplete="off" />
                            <PrimaryButton type="button" @click.prevent="copyToClipboard">
                                Copy
                            </PrimaryButton>
                            <SecondaryButton type="button" @click.prevent="resetStyleCode">
                                Reset
                            </SecondaryButton>
                        </div>
                        <p v-if="state.copyFeedback" class="text-xs mt-1">
                            {{ state.copyFeedback }}
                        </p>
                    </div>
                </template>
            </FormSection>
        </div>
    </AppLayout>
</template>
